/**
 * Platform abstraction layer for CampusHub.
 *
 * Centralises every "am I running in a native Capacitor shell or a plain
 * browser?" decision so the rest of the codebase never has to import
 * `@capacitor/core` directly. Two upsides:
 *
 *   1. Tree-shaking — the Capacitor runtime is a few KB but its plugins
 *      pull in iOS/Android polyfills. Keeping plugin imports inside
 *      lazily-loaded helpers (`getCamera()` etc.) means a pure web
 *      build never pays the cost.
 *   2. Mockability — the auth-store test suite can stub `isNative()` to
 *      `false` without touching `@capacitor/core` symbols.
 *
 * Anything that's strictly Capacitor-specific (Preferences, status bar,
 * keyboard) lives in dedicated modules in this folder. This file only
 * exposes detection + environment plumbing.
 */
import { Capacitor } from "@capacitor/core";

/**
 * `true` when the app runs inside an iOS / Android Capacitor shell.
 *
 * Capacitor's `isNativePlatform()` returns true on real devices and the
 * iOS Simulator / Android Emulator. SSR / Node / pure browsers return
 * `false`. Safe to call before React mounts.
 */
export function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    // `Capacitor.isNativePlatform` is synchronous so the catch is mostly
    // defensive for the JSDOM test env where the global isn't set up.
    return false;
  }
}

/**
 * `true` when the page is running as an installed PWA (home-screen launch
 * with no browser chrome). Covers both the standard `display-mode: standalone`
 * media query (Android Chrome, desktop Edge, Safari iOS 16.4+) and Apple's
 * older `navigator.standalone` API which iOS still sets for backward compat.
 *
 * Treat the same as `isNative()` for surfaces that don't make sense in a
 * marketing context (signup, brand panels, "create account" links) — once a
 * user has bothered to install the app to their home screen, they're a
 * tenant member, not a prospect.
 */
export function isStandalonePWA(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  } catch {
    // matchMedia can throw in very old browsers; ignore.
  }
  // iOS Safari still uses the legacy navigator.standalone flag.
  const navStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return Boolean(navStandalone);
}

/**
 * `true` for native shells OR installed PWAs. Use for "should this surface
 * look like an app?" decisions (login screen, no-marketing routing, FAB
 * navigation patterns). Marketing/SEO pages should use `isNative()` only.
 */
export function isAppLike(): boolean {
  return isNative() || isStandalonePWA() || isAppViewForced();
}

/** localStorage flag behind the `?appview=` override. */
const APPVIEW_KEY = "ch-appview";
/** Resolved once per page load — `isAppLike()` runs on nearly every render. */
let appViewForced: boolean | null = null;

/**
 * Debug / support override: show the APP's interface in an ordinary browser.
 *
 * `?appview=1` turns it on and remembers it, `?appview=0` turns it off again.
 * Nothing else reaches it — a normal visitor never has the flag, so this
 * changes what nobody sees until somebody asks for it.
 *
 * It exists because the app's dashboards, dock, drawer and native chrome are
 * real component branches rather than a responsive stylesheet, so the only
 * way to look at them used to be a full Capacitor build onto a simulator —
 * minutes per iteration, and no devtools worth the name. With this, a phone
 * browser (or a desktop one at phone width) renders exactly what the app
 * renders, inspector and all. `isAppLike()` is also what puts `native-app` on
 * <html>, so the native CSS comes with it rather than needing its own switch.
 *
 * Deliberately NOT width-based. Making a narrow viewport imply the app view
 * would silently change what every mobile-web visitor sees, which is a
 * product decision and not a debugging aid.
 */
export function isAppViewForced(): boolean {
  if (appViewForced !== null) return appViewForced;
  if (typeof window === "undefined") return false;
  appViewForced = (() => {
    try {
      const wanted = new URLSearchParams(window.location.search).get("appview");
      if (wanted === "1" || wanted === "on") {
        localStorage.setItem(APPVIEW_KEY, "1");
        return true;
      }
      if (wanted === "0" || wanted === "off") {
        localStorage.removeItem(APPVIEW_KEY);
        return false;
      }
      return localStorage.getItem(APPVIEW_KEY) === "1";
    } catch {
      // Private mode / blocked storage: the override simply is not available.
      return false;
    }
  })();
  return appViewForced;
}

/** Test seam — forget the resolved override so the next call re-reads it. */
export function resetAppViewForcedForTests(): void {
  appViewForced = null;
}

export function isAndroid(): boolean {
  return isNative() && Capacitor.getPlatform() === "android";
}

/**
 * `true` when the app should run in a low-power "perf-lite" visual mode:
 * no continuous ambient animations, no heavy blur, static premium gradients.
 *
 * Enabled for native shells (the WebView re-rasterizes blurred/animated
 * compositing layers every frame — the primary cause of the app running hot
 * and draining battery) and for any user who has asked the OS to reduce
 * motion. Desktop browsers and installed PWAs keep the full animated look,
 * so we gate on `isNative()` rather than `isAppLike()`.
 */
export function isPerfLite(): boolean {
  if (isNative()) return true;
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
  } catch {
    return false;
  }
}

/**
 * Scale a react-query `refetchInterval` for the current platform. On the
 * native app we slow fast polls down to at least 30s so a screenful of
 * timers doesn't keep the CPU/radio busy (heat + battery); on the web the
 * base interval is returned unchanged.
 */
export function nativePollMs(baseMs: number): number {
  return isNative() ? Math.max(baseMs, 30_000) : baseMs;
}

/**
 * Best-effort platform string. Returns `"web"` in the browser.
 *
 * Use for analytics/Sentry tags only — for behaviour gating, prefer the
 * boolean helpers above so misspellings don't silently break.
 */
export function getPlatform(): "ios" | "android" | "web" {
  if (!isNative()) return "web";
  const p = Capacitor.getPlatform();
  return p === "ios" || p === "android" ? p : "web";
}

/**
 * The HTTP origin the app should target for `/api` calls.
 *
 * Resolution order:
 *
 *   1. `VITE_API_URL` env (works in both web and native builds — set it
 *      to your production API origin for release builds).
 *   2. On native without an env var: an explicit error in dev / fallback
 *      to `https://api.campushub.ai` in prod. We deliberately do not
 *      fall back to `/api` on native — relative URLs resolve against
 *      `https://localhost` (Capacitor's WebView origin) and would never
 *      hit the real API.
 *   3. On web: `""` so axios falls through to the Vite dev proxy / same
 *      origin path.
 *
 * The returned value never has a trailing slash.
 */
export function getApiOrigin(): string {
  const env = (import.meta.env["VITE_API_URL"] as string | undefined)?.replace(/\/+$/, "");
  if (env) return env;
  if (isNative()) {
    // Live-reload dev: WebView origin is the Vite server — use relative
    // `/api/v1` so requests hit Vite's proxy (avoids CORS to staging/LAN).
    if (import.meta.env.DEV) {
      return "";
    }
    return "https://api.campushub.ai";
  }
  return "";
}

